import React, { useRef, useEffect, useMemo } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Box } from "@mui/material";
import Heading from "../heading/Heading";

const generateUniqueId = () =>
  `custom-toolbar-${Math.random().toString(36).substring(2, 9)}`;

export default function AdvancedInputField({
  handleChange = () => { },
  data = "",
  handleCheckedDataChanged = () => { },
  disableEdit = false,
  name = "",
  heading = "",
  placeholder = "Enter here",
  height = "100px",
  titleBar = true,
  component,
  testingId = "type-one-textbox",
  dataTestId = "type-one-textbox",
    onKeyDown = () => {},
}) {
  const toolbarId = useMemo(() => generateUniqueId(), []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: `#${toolbarId}`,
      },
    }),
    [toolbarId]
  );

  const quillRef = useRef(null);
  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    const editorEl = quillRef.current?.editor?.container?.querySelector(".ql-editor");
    if (editorEl) {
      editorEl.setAttribute("data-testid", dataTestId);
      editorEl.setAttribute("id", testingId);
      editorEl.setAttribute("role", "textbox"); // standard ARIA role
      editorEl.setAttribute("contenteditable", "true"); // this makes it editable
      editorEl.setAttribute("aria-multiline", "true"); // if multiline input

      // Handle paste event to strip color and background styles
      const handlePaste = (e) => {
        e.preventDefault();
        const clipboardData = e.clipboardData || window.clipboardData;
        const html = clipboardData.getData('text/html');

        if (html) {
          // Create a temporary div to parse the HTML
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = html;

          // Remove all color and background-color styles inline
          const removeStyles = (element) => {
            if (element.style) {
              element.style.color = '';
              element.style.backgroundColor = '';
              element.style.background = '';
            }
            // Also process children
            Array.from(element.children).forEach(removeStyles);
          };

          removeStyles(tempDiv);

          // Get the cleaned HTML and insert it
          const cleanHtml = tempDiv.innerHTML;

          // Insert the cleaned HTML into Quill
          const range = editor.getSelection(true);
          editor.clipboard.dangerouslyPasteHTML(range.index, cleanHtml);
        } else {
          // Fallback to plain text paste
          const text = clipboardData.getData('text/plain');
          const range = editor.getSelection(true);
          editor.insertText(range.index, text);
        }
      };

      editorEl.addEventListener('paste', handlePaste);

      // Cleanup listener on unmount
      return () => {
        editorEl.removeEventListener('paste', handlePaste);
      };
    }
  }, []);


  return (
    <>
      {heading && (
        <Heading text={heading} size="13px" marginBottom="10px" color="#999" />
      )}
      <Box
        sx={{
          backgroundColor: "#fff",
          borderRadius: "8px",
          // border: "2px solid rgb(92, 157, 226)",
          border: titleBar ? "2px solid #dadee3" : "none",
          padding: titleBar ? "8px" : 0,
          // maxWidth: 600,
          color: "white",
          width: "100%",
          "& .ql-container.ql-snow": {
            border: titleBar ? "1px solid #ccc" : "none !important",
          },
          "& .ql-editor": {
            minHeight: height, // ✅ This controls the rows height
            color: "#222",
            fontSize: "16px",
            padding: titleBar ? "interit" : "7px 0",
            border: titleBar ? "1px solid #ccc" : "none !important",
          },
          marginBottom: titleBar ? "20px" : 0,
        }}
      >
        {/* Custom Toolbar */}
        <Box
          id={toolbarId}
          sx={{ display: "flex", gap: 1, mb: titleBar ? 1 : 0 }}
          position={"relative"}
        >
          {titleBar && (
            <Box>
              <button className="ql-bold" />
              <button className="ql-italic" />
              <button className="ql-strike" />
              <button className="ql-link" />
              <button className="ql-list" value="ordered" />
              <button className="ql-list" value="bullet" />
              <button className="ql-align" value="" />
              <button className="ql-align" value="center" />
              <button className="ql-align" value="right" />
              <button className="ql-align" value="justify" />
              <Box position={"absolute"} right={0} top={0}>
                {!!component && component}
              </Box>
            </Box>
          )}

          {/* <button className="ql-code-block" />
        <button className="ql-clean" /> */}
        </Box>

        {/* Editor */}
        {/* <div id={testingId} data-testid={dataTestId}> */}
        <ReactQuill
          theme="snow"
          ref={quillRef}
          value={data || ""}
          onKeyDown={onKeyDown}
          onChange={(value) => {
            handleChange({ name, value });
            handleCheckedDataChanged();
          }}
          modules={titleBar ? modules : { toolbar: false }}
          placeholder={placeholder}
          style={{
            backgroundColor: "#fff",
            color: "#222",
            border: "none",
            padding: 0,
          }}
          readOnly={disableEdit}
        />
        {/* </div> */}
      </Box>
    </>
  );
}
